import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Card,
  Grid,
  IconButton,
  Paper,
  type SelectChangeEvent,
  type SelectProps,
  Stack,
  styled,
  TextField,
  Typography,
} from "~/common/mui";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";

import { useTranslation } from "react-i18next";
import { PopperTooltip } from "~/common/components/Tooltip";
import { MMDFilterSkeleton, MMDTableSkeleton } from "~/features/productDetails/components/MMDAssistant/MMDSkeleton";
import classes from "~/features/productDetails/components/MMDAssistant/MMDAssistant.module.less";
import { Dialog, DialogActions, DialogContent, DialogHeader } from "~/common/mui/Dialog/Dialog";
import { C_PRIMARY, C_SURFACE_BRIGHT } from "~/common/components/icon/Colors";
import DateInput from "~/common/components/DateInput";
import CustomNumberFormat from "~/common/components/CustomNumberFormat";
import PriceReferenceDropdown from "~/features/productDetails/components/AddToCartCard/PriceReferenceDropdown";
import { SimpleMultiSelectItem } from "~/common/components/Select/SimpleSelect";
import MMDResultsTable from "~/features/productDetails/components/MMDAssistant/components/MMDResultsTable";
import { Controller, type ControllerRenderProps, FormProvider, useForm, useFormContext } from "react-hook-form";
import {
  fetchMMDAssistantData,
  type MMDAssistantCalculationResultDTO,
  type MMDAssistantResponseItemDTO,
} from "~/features/productDetails/components/MMDAssistant/api/fetchMMDAssistantData";
import type {
  MMDTableData,
  MMDTableResultItem,
} from "~/features/productDetails/components/MMDAssistant/MMDAssistant.types";
import { sum } from "~/utils/PriceUtils";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { useSelector, useStore } from "react-redux";
import type { RootState } from "~/app/store";
import { getTotalPrice, selectNetPrice } from "~/domains/product/utils/productDescriptorUtils";
import { simpleAddToCart } from "~/features/addToCart/utils/SimpleAddToCart";
import type { Store } from "redux";
import { CheckedCircle } from "~/common/components/CheckedCircle/CheckedCircle";
import { ExpandMore } from "@mui/icons-material";
import Tag from "~/common/components/Tag/Tag";
import { debounce } from "lodash";
import { uniqueValues } from "~/common/utils/misc";
import { resolveUrl } from "~/utils/url/urlResolver";
import { getFirstValidDate, isDateInvalidForDelivery } from "~/common/utils/dateUtils";
import { requestConfirmation } from "~/common/utils/ModalService";
import DeleteCartConfirmationModal from "~/domains/cart/components/DeleteCartConfirmationModal";
import { userCanSeePriceReference } from "~/features/cart/cart/components/LineItemStaticParams";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { CART_PAGE } from "~/utils/url/staticUrls";
import { usePurchasePlace } from "~/app/analytics/ga/recommendations/RecommendationContexts";

interface MMDDialogProps {
  marketDesignation: string;
  open: boolean;
  onClose: () => void;
  initialData?: Partial<MMDFormValues>;
}

export type FilterNames = "designations" | "packaging" | "customerMaterialNumbers";

export interface MMDFormValues {
  filterOrder: FilterNames[];
  designations: string[];
  packaging: string[];
  customerMaterialNumbers: string[];
  quantity: number;
  rdd: Date | null;
  priceReference: string;
  isOverride?: boolean;
}

export default function MMDDialog({ open, onClose, marketDesignation, initialData = {} }: MMDDialogProps) {
  const { t } = useTranslation();
  const {
    loadingFilters,
    loadingTable,
    formMethods,
    filterOptions,
    calculationResults,
    onBlur,
    selectedTable,
    setSelectedTable,
    hasFilters,
  } = useMMDLogic(marketDesignation, open, initialData);

  const showOverrideBtn = useSelector((state: RootState) => state.minicart.count > 0);
  const showNoResults =
    calculationResults.length === 0 ||
    (hasFilters && calculationResults[0].title !== "feature.mmd.results.filtered.title");
  const disableAddToCart = calculationResults.length === 0;
  const page = usePurchasePlace();

  const handleAddToCart = async (isOverride: boolean) => {
    if (isOverride) {
      const confirmed = await requestConfirmation(({ onCancel, onConfirm }) => (
        <DeleteCartConfirmationModal onCancel={onCancel} onOk={onConfirm} />
      ));
      if (!confirmed) {
        return;
      }
    }
    const sourceTableData = calculationResults[selectedTable];
    const priceReference = formMethods.getValues("priceReference");
    const rdd = formMethods.getValues("rdd");
    const items = sourceTableData!.items.map((item) => ({
      productCode: item.productCode,
      packCode: item.packCode,
      quantity: item.quantity,
      priceReference,
      rdd,
      isSale: false,
    }));
    await simpleAddToCart(items, { page, isOverride, showSuccessDrawer: false });
    onClose();
    redirectToUrl(CART_PAGE);
  };

  let tables: ReactNode[] = [];
  if (!loadingTable) {
    tables = calculationResults.map((result, index) => (
      <TableContainer
        key={result.title}
        defaultExpanded={index === 0}
        selectable={calculationResults.length > 1}
        selected={selectedTable === index}
        onSelect={() => setSelectedTable(index)}
        title={t(result.title)}
        tag={result.tag ? <Tag variant="success">{t(result.tag)}</Tag> : undefined}
        productAmount={result.totalDifferentProducts}
      >
        <MMDResultsTable {...result} />
      </TableContainer>
    ));

    if (showNoResults) {
      tables.unshift(<Alert color="error">{t("feature.mmd.assistant.table.noResultsForFilter")}</Alert>);
    }
  }

  return (
    <Dialog onClose={onClose} maxWidth={"desktop-l"} open={open}>
      <StyledDialogHeader onClose={onClose}>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <Typography variant={"headlineSmall"}>{t("feature.mmd.assistant.dialog.title")}</Typography>

          <PopperTooltip
            placement={"bottom-start"}
            contentClassName={classes.tooltipOuter}
            innerClassName={classes.tooltipInner}
            className={classes.tooltip}
            cardVariant={"rounded"}
            tooltipContent={
              <div className={classes.tooltipContentContainer}>
                <div className={classes.tooltipContent}>{t("feature.mmd.assistant.dialog.info")}</div>
                <div className={classes.tooltipAction}>
                  <Button variant="text" href={resolveUrl("/market-material-designation")}>
                    {t("newsletter.consent.seemore")}
                  </Button>
                </div>
              </div>
            }
          >
            <InfoOutlineIcon fontSize="medium" />
          </PopperTooltip>
        </Stack>
      </StyledDialogHeader>

      <StyledDialogContent>
        <FormProvider {...formMethods}>
          <Stack gap={2}>
            {loadingFilters ? <MMDFilterSkeleton /> : <Filters {...filterOptions} onBlur={onBlur} />}

            {loadingTable && (
              <TableContainer productAmount={0}>
                <MMDTableSkeleton />
              </TableContainer>
            )}

            {tables}

            {/* {!loadingTable && !tableData && (
              <Alert color="error">{t("feature.mmd.assistant.table.noResultsForFilter")}</Alert>
            )} */}
          </Stack>
        </FormProvider>
      </StyledDialogContent>

      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button variant="text" onClick={onClose}>
          {t("bulkUpload.buttons.cancel")}
        </Button>
        <Stack direction={"row"} gap={1}>
          {showOverrideBtn && (
            <Button variant="text" onClick={() => handleAddToCart(true)} disabled={disableAddToCart}>
              {t("bulkUpload.buttons.override")}
            </Button>
          )}
          <Button onClick={() => handleAddToCart(false)} disabled={disableAddToCart}>
            {t("bulkUpload.buttons.add")}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

const Filters = ({
  products,
  packages,
  customerMaterialNumbers,
  onBlur,
}: FilterOptionsData & { onBlur: () => void }) => {
  const { t } = useTranslation();
  const { control, setValue, getValues } = useFormContext<MMDFormValues>();

  const makeRenderValue = (placeholder: string, values: OptionData[], isAllSelected: boolean) => {
    return (value: unknown) => {
      const castedValue = value as string[];
      if (castedValue.length === 0 || isAllSelected) {
        return placeholder;
      }
      const labels = castedValue.map((value) => values.find((v) => v.value === value)?.label);
      return labels.join(", ");
    };
  };

  const makeOnChange = (
    fieldName: FilterNames,
    valueAmount: number,
    externalOnChange: SelectProps<string[]>["onChange"],
  ) => {
    return (event: SelectChangeEvent<string[]>, child: React.ReactNode) => {
      const selected = event.target.value.length > 0;
      const selectedFilters = getValues("filterOrder");
      if (selected && !selectedFilters.includes(fieldName)) {
        setValue("filterOrder", [...selectedFilters, fieldName]);
      } else if (!selected && selectedFilters.includes(fieldName)) {
        setValue(
          "filterOrder",
          selectedFilters.filter((filter) => filter !== fieldName),
        );
      }
      externalOnChange?.(event, child);
    };
  };

  const makeRenderSelect =
    <TFieldName extends FilterNames>(fieldName: TFieldName, placeholderKey: string, options: OptionData[]) =>
    ({ field }: { field: ControllerRenderProps<MMDFormValues, TFieldName> }) => {
      const disabled = options.every((option) => option.disabled);
      const isAnythingSelected = field.value.length > 0;
      const placeholder =
        disabled && !isAnythingSelected ? "-" : t(`feature.mmd.assistant.filters.${placeholderKey}.placeholder`);
      const renderValue = makeRenderValue(placeholder, options, options.length === field.value.length);
      const onChange = makeOnChange(fieldName, options.length, field.onChange);

      return (
        <TextField
          disabled={disabled}
          select
          size={"small"}
          fullWidth
          label={t(`feature.mmd.assistant.filters.${placeholderKey}.label`)}
          SelectProps={{
            multiple: true,
            value: field.value,
            // @ts-expect-error
            onChange: onChange,
            displayEmpty: true,
            renderValue: renderValue,
          }}
          InputLabelProps={{ shrink: true }}
          onBlur={onBlur}
        >
          {options.map((option) => (
            <SimpleMultiSelectItem key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </SimpleMultiSelectItem>
          ))}
        </TextField>
      );
    };

  return (
    <Paper sx={{ background: C_SURFACE_BRIGHT, padding: "16px" }}>
      <Grid container size={12} spacing={2}>
        <Grid size={12}>
          <Typography variant="bodyLarge">{t("feature.mmd.assistant.filters.title.scope")}</Typography>
        </Grid>
        <Grid size={4}>
          {products.length > 0 && (
            <Controller
              name="designations"
              control={control}
              render={makeRenderSelect("designations", "product", products)}
            />
          )}
        </Grid>

        <Grid size={4}>
          {packages.length > 0 && (
            <Controller
              name="packaging"
              control={control}
              render={makeRenderSelect("packaging", "package", packages)}
            />
          )}
        </Grid>

        <Grid size={4}>
          {customerMaterialNumbers.length > 0 && (
            <Controller
              name="customerMaterialNumbers"
              control={control}
              render={makeRenderSelect("customerMaterialNumbers", "alias", customerMaterialNumbers)}
            />
          )}
        </Grid>

        <Grid size={12}>
          <Typography variant="bodyLarge">{t("feature.mmd.assistant.filters.title.requirements")}</Typography>
        </Grid>
        <Grid size={4}>
          <Controller
            name="quantity"
            control={control}
            rules={{
              required: t("feature.mmd.assistant.filters.error.quantity.required"),
              min: {
                value: 1,
                message: t("feature.mmd.assistant.filters.error.quantity.min"),
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomNumberFormat
                label={t("feature.mmd.assistant.filters.quantity")}
                value={value ?? null}
                handleState={(e) => onChange(e.floatValue ?? null)}
                size={"small"}
                onBlur={onBlur}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>

        <Grid size={4}>
          <Controller
            name="rdd"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DateInput
                date={value}
                onChange={onChange}
                size={"small"}
                onBlur={onBlur}
                label={t("feature.mmd.assistant.filters.rdd")}
                isDayDisabled={isDateInvalidForDelivery}
              />
            )}
          />
        </Grid>

        {userCanSeePriceReference() && (
          <Grid size={4}>
            <Controller
              name="priceReference"
              control={control}
              render={({ field: { onChange, value } }) => (
                <PriceReferenceDropdown selected={value} onChange={onChange} size={"small"} onBlur={onBlur} />
              )}
            />
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

type OptionData = {
  label: string;
  value: string;
  disabled?: boolean;
};
interface FilterOptionsData {
  products: OptionData[];
  packages: OptionData[];
  customerMaterialNumbers: OptionData[];
}

function useMMDLogic(marketDesignation: string, open: boolean, initialData: Partial<MMDFormValues>) {
  const { t } = useTranslation();
  const store = useStore<RootState>();
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [calculationResults, setCalculationResults] = useState<MMDTableData[]>([]);
  const [selectedTable, setSelectedTable] = useState<number>(0);

  const [filterOptions, setFilterOptions] = useState<FilterOptionsData>(() => ({
    products: [],
    packages: [],
    customerMaterialNumbers: [],
    quantity: 1,
  }));

  const formMethods = useForm<MMDFormValues>({
    defaultValues: makeInitialData(initialData),
  });

  formMethods.register("filterOrder");

  const filtersLoaded = filterOptions.products.length > 0;
  const onSubmit = useMemo(() => {
    return debounce(async (data: MMDFormValues) => {
      if (!filtersLoaded) {
        setLoadingFilters(true);
      }

      setLoadingTable(true);

      const { results: items, filterOptions } = await fetchMMDAssistantData({ ...data, marketDesignation });

      setFilterOptions(() => {
        return {
          products: filterOptions.products.map((product) => ({
            label: product.value,
            value: product.value,
            disabled: product.disabled,
          })),
          packages: filterOptions.packaging.map((packaging) => ({
            label: t("feature.mmd.assistant.perPack", { amount: packaging.value }),
            value: packaging.value,
            disabled: packaging.disabled,
          })),
          customerMaterialNumbers: filterOptions.customerMaterialNumbers.map((cmn) => ({
            label: cmn.value,
            value: cmn.value,
            disabled: cmn.disabled,
          })),
        };
      });
      setLoadingFilters(false);

      (async () => {
        const calculationResults = await Promise.all(
          items.map((calculationResult) => transformCalculationResult(calculationResult, data.priceReference, store)),
        );
        setCalculationResults(calculationResults);

        setSelectedTable(0);
        setLoadingTable(false);
      })();
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersLoaded]);

  const getFormKey = () => JSON.stringify(formMethods.watch());

  useEffect(() => {
    if (!open) {
      return;
    }
    formMethods.handleSubmit(onSubmit)();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getFormKey(), open]);

  const onBlur = () => {
    onSubmit.flush();
  };

  const hasFilters = formMethods.getValues("filterOrder").length > 0;

  return {
    formMethods,
    loadingFilters,
    loadingTable,
    filterOptions,
    calculationResults,
    onBlur,
    selectedTable,
    setSelectedTable,
    hasFilters,
  };
}

async function transformCalculationResult(
  calculationResult: MMDAssistantCalculationResultDTO,
  priceReference: string,
  store: Store<RootState>,
): Promise<MMDTableData> {
  const qtyPerProductCode = calculationResult.data.reduce(
    (acc, item) => {
      acc[item.productCode] = (acc[item.productCode] || 0) + item.quantity;
      return acc;
    },
    {} as Record<string, number>,
  );

  async function transformTableDataItem(item: MMDAssistantResponseItemDTO): Promise<MMDTableResultItem> {
    // Await so that the data is added to store
    await ProductService.getProductDescriptor(item.productCode, priceReference, {
      fetch: true,
    }).promise!;

    const extraQuantity = qtyPerProductCode[item.productCode];

    const netPrice = selectNetPrice({
      productCode: item.productCode,
      packCode: item.packCode,
      priceReference: priceReference,
      extraQuantity,
    })(store.getState());

    const totalPrice = getTotalPrice(netPrice, item.quantity);

    return {
      esd: item.estimatedShippingDate ? new Date(item.estimatedShippingDate) : null,
      productCode: item.productCode,
      designation: item.productDesignation,
      materialNo: item.sapMaterialNumber,
      packCode: item.packCode,
      packSize: item.packSize,
      customerMaterialNo: item.customerMaterialNumber.join(", "),
      quantity: item.quantity,
      netPrice: netPrice,
      netValue: totalPrice,
    };
  }

  const tableRows = await Promise.all(calculationResult.data.map(transformTableDataItem));

  return {
    title: calculationResult.title,
    tag: calculationResult.tag,
    items: tableRows,
    totalNetValue: sum(...tableRows.map((item) => item.netValue)),
    totalQuantity: tableRows.reduce((acc, item) => acc + item.quantity, 0),
    totalDifferentProducts: uniqueValues(tableRows.map((item) => item.productCode)).length,
  };
}

interface TableContainerProps {
  children: ReactNode;
  title?: string;
  tag?: ReactNode;

  selectable?: boolean;
  selected?: boolean;
  productAmount: number;
  onSelect?: () => void;
  defaultExpanded?: boolean;
}

const TableContainer = ({
  children,
  selectable,
  selected,
  onSelect,
  title,
  tag,
  productAmount,
  defaultExpanded,
}: TableContainerProps) => {
  const { t } = useTranslation();

  const handleSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect?.();
  };

  const tableTitle = !!title && (
    <Stack direction={"row"} alignItems={"center"} gap={1} sx={{ fontWeight: "normal" }}>
      <IconButton onClick={handleSelect} disabled={!selectable}>
        <CheckedCircle checked={selected} alwaysVisible />
      </IconButton>

      <Typography variant="bodyLarge">{title}</Typography>
      <Typography variant="bodySmall" color="text.label">
        {t("feature.mmd.assistant.dialog.material", { count: productAmount })}
      </Typography>
      {tag}
    </Stack>
  );

  if (!selectable) {
    return (
      <Card sx={{ padding: "8px 16px 16px" }}>
        <Stack gap={1}>
          {tableTitle}
          {children}
        </Stack>
      </Card>
    );
  }

  return (
    <Card sx={{ borderColor: selectable && selected ? C_PRIMARY : undefined }}>
      <StyledAccordion defaultExpanded={defaultExpanded}>
        <AccordionSummary expandIcon={<ExpandMore />}>{tableTitle}</AccordionSummary>
        <AccordionDetails sx={{ padding: "8px 16px 16px" }}>{children}</AccordionDetails>
      </StyledAccordion>
    </Card>
  );
};

const StyledAccordion = styled(Accordion)({
  ["&:before"]: {
    display: "none !important",
  },
  ["&:after"]: {
    display: "none !important",
  },
});

// TODO - This should be part of generic dialog styles.

const StyledDialogHeader = styled(DialogHeader)({
  paddingBottom: "16px",
  marginBottom: "0",
});

const StyledDialogContent = styled(DialogContent)({
  paddingTop: "0",
});

function makeInitialData(data: Partial<MMDFormValues>): MMDFormValues {
  let filterOrder: FilterNames[] = [];
  if ("customerMaterialNumbers" in data) {
    filterOrder = ["customerMaterialNumbers"];
  }
  return {
    designations: [],
    packaging: [],
    customerMaterialNumbers: [],
    quantity: 1,
    rdd: getFirstValidDate(),
    priceReference: "",
    filterOrder,
    ...data,
  };
}
